import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import SignaturePad from '@/components/admin/SignaturePad';
import TrimbleAddressAutocomplete from '@/components/TrimbleAddressAutocomplete';
import logoLight from '@/assets/logo-horizontal-light.png';

const TAX_YEAR = 2026;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID',
  'IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO',
  'MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA',
  'RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

const FILING_STATUSES = [
  { value: 'single_or_mfs', label: 'Single or Married filing separately' },
  { value: 'married_jointly', label: 'Married filing jointly or Qualifying surviving spouse' },
  { value: 'head_of_household', label: 'Head of household' },
];

const StepHeader = ({ n, title }) => (
  <div className="flex items-center gap-2 pb-2 border-b border-border">
    <span className="w-5 h-5 rounded-full bg-prohaul-black/10 text-prohaul-black text-xs font-bold flex items-center justify-center flex-shrink-0">{n}</span>
    <h3 className="text-sm font-semibold text-foreground">{title}</h3>
  </div>
);

const formatSSN = (raw) => {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Mandatory Form W-4 (2026) capture, shown right after
// CompleteStaffProfileGate -- an employer legally needs a signed W-4
// on file before payroll can withhold federal income tax correctly,
// and there was previously no way to collect one at all. This is a
// faithful digital replica of Steps 1-5 of the official form (not the
// page 3/4 worksheets or page 5 tax tables -- those are reference
// aids a filer keeps for their own records, not fields submitted to
// the employer). SSN lives in a dedicated, tightly-RLS'd table
// (0032_staff_w4_forms.sql) instead of staff_profiles, which any
// active carrier member can already read.
export default function CompleteW4Gate({ profile, onComplete }) {
  // See the matching comment in CompleteStaffProfileGate.jsx -- a legal tax
  // form needs to read clearly regardless of a brand-new user's (possibly
  // just-defaulted-to-dark) theme preference.
  useEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains('dark');
    root.classList.remove('dark');
    return () => { if (wasDark) root.classList.add('dark'); };
  }, []);

  const [firstName, setFirstName] = useState(profile.first_name || '');
  const [middleInitial, setMiddleInitial] = useState('');
  const [lastName, setLastName] = useState(profile.last_name || '');
  const [address, setAddress] = useState(profile.address || '');
  const [city, setCity] = useState(profile.city || '');
  const [state, setState] = useState(profile.state || '');
  const [zip, setZip] = useState(profile.zip || '');
  const [ssn, setSsn] = useState('');
  const [filingStatus, setFilingStatus] = useState('');
  const [multipleJobs, setMultipleJobs] = useState(false);
  const [qualifyingDependents, setQualifyingDependents] = useState('');
  const [otherDependents, setOtherDependents] = useState('');
  const [otherCredits, setOtherCredits] = useState('');
  const [otherIncome, setOtherIncome] = useState('');
  const [deductions, setDeductions] = useState('');
  const [extraWithholding, setExtraWithholding] = useState('');
  const [exempt, setExempt] = useState(false);
  const [signature, setSignature] = useState('');
  const [saving, setSaving] = useState(false);

  const step3Total = (Number(qualifyingDependents || 0) * 2200) + (Number(otherDependents || 0) * 500) + Number(otherCredits || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) { toast.error('First and last name are required.'); return; }
    if (!address.trim() || !city.trim() || !state || !zip.trim()) { toast.error('Full address is required.'); return; }
    if (ssn.replace(/\D/g, '').length !== 9) { toast.error('Enter a valid 9-digit Social Security Number.'); return; }
    if (!exempt && !filingStatus) { toast.error('Select a filing status.'); return; }
    if (!signature) { toast.error('Sign the form to continue.'); return; }

    setSaving(true);
    try {
      await client.entities.StaffW4Form.create({
        user_id: profile.user_id,
        tax_year: TAX_YEAR,
        signature,
        signed_at: new Date().toISOString(),
        first_name: firstName.trim(),
        middle_initial: middleInitial.trim(),
        last_name: lastName.trim(),
        address: address.trim(),
        city: city.trim(),
        state,
        zip: zip.trim(),
        ssn: ssn.replace(/\D/g, ''),
        filing_status: exempt ? null : filingStatus,
        multiple_jobs_checkbox: exempt ? false : multipleJobs,
        dependents_qualifying_count: exempt ? 0 : Number(qualifyingDependents || 0),
        dependents_other_count: exempt ? 0 : Number(otherDependents || 0),
        other_credits: exempt ? 0 : Number(otherCredits || 0),
        other_income: exempt ? 0 : Number(otherIncome || 0),
        deductions: exempt ? 0 : Number(deductions || 0),
        extra_withholding: exempt ? 0 : Number(extraWithholding || 0),
        exempt,
      });
      toast.success('W-4 submitted.');
      onComplete();
    } catch (err) {
      toast.error(err?.message || 'Could not save your W-4.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-2xl space-y-6">
        <div className="text-center">
          <img src={logoLight} alt="Pro Haul" className="w-56 mx-auto mb-6" />
          <h1 className="font-space font-bold text-xl text-foreground">Form W-4 ({TAX_YEAR})</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Employee's Withholding Certificate -- required so we can withhold the correct federal income tax from your pay.
          </p>
        </div>

        <form onSubmit={submit} className="glass-card p-5 space-y-6">
          {/* Step 1 */}
          <div className="space-y-3">
            <StepHeader n={1} title="Personal Information" />
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs text-muted-foreground block mb-1">First Name *</label>
                <Input value={firstName} onChange={e => setFirstName(e.target.value)} className="text-foreground" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">M.I.</label>
                <Input value={middleInitial} onChange={e => setMiddleInitial(e.target.value.slice(0, 1))} className="text-foreground" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-muted-foreground block mb-1">Last Name *</label>
                <Input value={lastName} onChange={e => setLastName(e.target.value)} className="text-foreground" />
              </div>
            </div>
            <div>
              <TrimbleAddressAutocomplete
                value={address}
                onChange={e => setAddress(e.target.value)}
                onSelect={data => {
                  setAddress(data.street || address);
                  if (data.city) setCity(data.city);
                  if (data.state) setState(data.state);
                  if (data.zip) setZip(data.zip);
                }}
                label="Address *"
                placeholder="123 Main St"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <label className="text-xs text-muted-foreground block mb-1">City *</label>
                <Input value={city} onChange={e => setCity(e.target.value)} className="text-foreground" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">State *</label>
                <select
                  value={state}
                  onChange={e => setState(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none"
                >
                  <option value="" className="bg-background">—</option>
                  {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Zip *</label>
                <Input value={zip} onChange={e => setZip(e.target.value)} className="text-foreground" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Social Security Number *</label>
              <Input
                value={ssn}
                onChange={e => setSsn(formatSSN(e.target.value))}
                placeholder="000-00-0000"
                inputMode="numeric"
                className="text-foreground max-w-[200px]"
              />
              <p className="text-xs text-muted-foreground mt-1">Only visible to you and HR/Owner -- used for payroll tax withholding.</p>
            </div>

            <div>
              <p className="text-xs text-muted-foreground mb-1.5">Filing Status {!exempt && '*'}</p>
              <div className="space-y-1.5">
                {FILING_STATUSES.map(fs => (
                  <label key={fs.value} className={cn("flex items-center gap-2 text-sm cursor-pointer", exempt && "opacity-40 pointer-events-none")}>
                    <input
                      type="radio"
                      name="filing_status"
                      checked={filingStatus === fs.value}
                      onChange={() => setFilingStatus(fs.value)}
                      disabled={exempt}
                      className="accent-prohaul-black"
                    />
                    <span className="text-foreground">{fs.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Exempt toggle up top since it short-circuits Steps 2-4 */}
          <label className={cn("flex items-start gap-2.5 p-3 rounded-xl border border-border cursor-pointer", exempt && "border-prohaul-black/30 bg-prohaul-black/5")}>
            <input type="checkbox" checked={exempt} onChange={e => setExempt(e.target.checked)} className="accent-prohaul-black mt-0.5" />
            <span className="text-xs text-foreground leading-relaxed">
              <span className="font-medium">I claim exemption from withholding for {TAX_YEAR}</span> -- I had no federal income tax liability last year and expect none this year. I understand I'll need to submit a new W-4 for {TAX_YEAR + 1}. (Skips Steps 2-4 below.)
            </span>
          </label>

          {!exempt && (
            <>
              {/* Step 2 */}
              <div className="space-y-2">
                <StepHeader n={2} title="Multiple Jobs or Spouse Works" />
                <p className="text-xs text-muted-foreground">Only if you hold more than one job at a time, or you're married filing jointly and your spouse also works.</p>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={multipleJobs} onChange={e => setMultipleJobs(e.target.checked)} className="accent-prohaul-black" />
                  <span className="text-foreground">There are only two jobs total (mine and/or my spouse's)</span>
                </label>
                <p className="text-xs text-muted-foreground">
                  For more complex situations (3+ jobs, or a more precise number), use the IRS estimator at{' '}
                  <a href="https://www.irs.gov/W4App" target="_blank" rel="noreferrer" className="text-blue-600 underline">irs.gov/W4App</a>{' '}
                  and enter the result in Step 4(c) below instead.
                </p>
              </div>

              {/* Step 3 */}
              <div className="space-y-3">
                <StepHeader n={3} title="Claim Dependents and Other Credits" />
                <p className="text-xs text-muted-foreground">If your total income is $200,000 or less ($400,000 or less if married filing jointly).</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Qualifying children under 17</label>
                    <Input type="number" min="0" value={qualifyingDependents} onChange={e => setQualifyingDependents(e.target.value)} className="text-foreground" placeholder="0" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Other dependents</label>
                    <Input type="number" min="0" value={otherDependents} onChange={e => setOtherDependents(e.target.value)} className="text-foreground" placeholder="0" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Other credits ($)</label>
                    <Input type="number" min="0" step="0.01" value={otherCredits} onChange={e => setOtherCredits(e.target.value)} className="text-foreground" placeholder="0.00" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Step 3 total: <span className="text-foreground font-medium">{money(step3Total)}</span></p>
              </div>

              {/* Step 4 */}
              <div className="space-y-3">
                <StepHeader n={4} title="Other Adjustments (optional)" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Other income, not from jobs ($)</label>
                    <Input type="number" min="0" step="0.01" value={otherIncome} onChange={e => setOtherIncome(e.target.value)} className="text-foreground" placeholder="0.00" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Deductions ($)</label>
                    <Input type="number" min="0" step="0.01" value={deductions} onChange={e => setDeductions(e.target.value)} className="text-foreground" placeholder="0.00" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Extra withholding per pay period ($)</label>
                    <Input type="number" min="0" step="0.01" value={extraWithholding} onChange={e => setExtraWithholding(e.target.value)} className="text-foreground" placeholder="0.00" />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Step 5 */}
          <div className="space-y-3">
            <StepHeader n={5} title="Sign Here" />
            <p className="text-xs text-muted-foreground">Under penalties of perjury, I declare that this certificate, to the best of my knowledge and belief, is true, correct, and complete.</p>
            <SignaturePad value={signature} onChange={setSignature} />
            <p className="text-xs text-muted-foreground">Date: {new Date().toLocaleDateString()}</p>
          </div>

          <Button type="submit" disabled={saving} className="w-full bg-prohaul-black text-prohaul-white hover:bg-prohaul-black/90">
            {saving ? 'Submitting…' : 'Submit W-4'}
          </Button>
        </form>
      </div>
    </div>
  );
}
